import {
  CircleDollarSign,
  Download,
  Eye,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PdfPreviewDialog } from '@/components/PdfPreviewDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangeFilter } from '@/components/ui/date-range-filter';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { EntitySelect } from '@/components/EntitySelect';
import { exportCsv, type ApiError } from '@/core/api';
import { resolveCompanyLabel, searchCompanies } from '@/features/crm/companies/select';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatMoney } from '@/lib/money';
import { BASE as INVOICES_BASE, getInvoiceBlob } from '../api';
import {
  INVOICE_DIRECTIONS,
  INVOICE_STATUSES,
  directionLabel,
  invoiceStatusLabel,
  invoiceStatusVariant,
} from '../constants';
import { useInvoices, useMarkInvoicePaid } from '../queries';
import { useInvoicesUi, type InvoicesViewMode } from '../store/viewPrefs';
import type { Invoice } from '../types';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type InvoiceOrderingColumn = 'number' | 'direction' | 'status' | 'base_total' | 'issue_date';

const VIEW_MODES: readonly ViewModeOption<InvoicesViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

/** Client label: the linked company, else the free-text counterparty. */
function partyName(inv: Invoice): string {
  return inv.company_name || inv.counterparty_name || '—';
}

export function InvoicesListPage() {
  const navigate = useNavigate();
  const view = useInvoicesUi((s) => s.view);
  const setView = useInvoicesUi((s) => s.setView);
  const ordering = useInvoicesUi((s) => s.ordering);
  const setOrdering = useInvoicesUi((s) => s.setOrdering);

  // Server-driven filters live in local state — pagination + search hit the API.
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [direction, setDirection] = useState('all');
  const [status, setStatus] = useState('all');
  const [company, setCompany] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useInvoices({
    page,
    search: search || undefined,
    direction: direction === 'all' ? undefined : direction,
    status: status === 'all' ? undefined : status,
    company: company ? Number(company) : undefined,
    date_from: from || undefined,
    date_to: to || undefined,
    ordering,
  });

  const results = data?.results ?? [];
  const open = (inv: Invoice) => navigate(`/finance/billing/${inv.public_id}`);

  // Inline PDF preview — client-invoice media is directly URL-loadable.
  const [preview, setPreview] = useState<Invoice | null>(null);
  const previewPublicId = preview?.public_id ?? null;
  const fetchPreviewPdf = useCallback(
    () => getInvoiceBlob(previewPublicId as string),
    [previewPublicId],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Client billing invoices — receivables you send to clients (and payables you owe).
            Staff payouts live under Staff Invoices.
          </p>
        </div>
        <Button onClick={() => navigate('/finance/billing/new')}>
          <Plus /> New invoice
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search number / party…"
              className="pl-8"
              aria-label="Search invoices"
            />
          </div>
          <Select
            value={direction}
            onValueChange={(v) => {
              setPage(1);
              setDirection(v);
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All directions</SelectItem>
              {INVOICE_DIRECTIONS.map((d) => (
                <SelectItem key={d.value} value={d.value}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={status}
            onValueChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {INVOICE_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="w-52">
            <EntitySelect
              queryKey="invoice-company-filter"
              value={company}
              onChange={(v: string) => {
                setPage(1);
                setCompany(v);
              }}
              search={searchCompanies}
              resolveLabel={resolveCompanyLabel}
              placeholder="All clients"
            />
          </div>
          <DateRangeFilter
            label="Issued"
            idPrefix="invoice-issued"
            from={from}
            to={to}
            onFromChange={(v) => {
              setPage(1);
              setFrom(v);
            }}
            onToChange={(v) => {
              setPage(1);
              setTo(v);
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `invoices.csv` without leaving the SPA.
              void exportCsv(INVOICES_BASE, {
                search: search || undefined,
                direction: direction === 'all' ? undefined : direction,
                status: status === 'all' ? undefined : status,
                date_from: from || undefined,
                date_to: to || undefined,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export invoices as CSV"
            title="Export invoices as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <InvoicesSkeleton />
      ) : results.length === 0 ? (
        <InvoicesTable
          invoices={[]}
          onRowClick={() => undefined}
          onView={setPreview}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <InvoicesTable
          invoices={results}
          onRowClick={open}
          onView={setPreview}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <InvoicesList invoices={results} onOpen={open} />
      ) : view === 'cards' ? (
        <InvoicesCards invoices={results} onOpen={open} />
      ) : (
        <InvoicesGroupedByStatus invoices={results} onOpen={open} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'invoice' : 'invoices'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <PdfPreviewDialog
        open={Boolean(preview)}
        onOpenChange={(o) => !o && setPreview(null)}
        title={`${preview?.number ?? 'Invoice'} — PDF`}
        fetchBlob={previewPublicId ? fetchPreviewPdf : undefined}
      />
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  invoices: Invoice[];
  onOpen: (inv: Invoice) => void;
}

interface InvoicesTableProps {
  invoices: Invoice[];
  onRowClick: (inv: Invoice) => void;
  onView: (inv: Invoice) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function InvoicesTable({ invoices, onRowClick, onView, ordering, onOrderingChange }: InvoicesTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<InvoiceOrderingColumn>
              column="number"
              value={ordering}
              onChange={onOrderingChange}
            >
              Number
            </SortableTableHead>
            <SortableTableHead<InvoiceOrderingColumn>
              column="direction"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Direction
            </SortableTableHead>
            <TableHead className="hidden md:table-cell">Party</TableHead>
            <SortableTableHead<InvoiceOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <SortableTableHead<InvoiceOrderingColumn>
              column="base_total"
              value={ordering}
              onChange={onOrderingChange}
            >
              Total (USD)
            </SortableTableHead>
            <SortableTableHead<InvoiceOrderingColumn>
              column="issue_date"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden lg:table-cell"
            >
              Issued
            </SortableTableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-muted-foreground py-10 text-center">
                No invoices found.
              </TableCell>
            </TableRow>
          ) : (
            invoices.map((inv) => (
              <TableRow key={inv.id} className="cursor-pointer" onClick={() => onRowClick(inv)}>
                <TableCell className="font-medium">{inv.number}</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {directionLabel(inv.direction)}
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {partyName(inv)}
                </TableCell>
                <TableCell>
                  <Badge variant={invoiceStatusVariant(inv.status)}>
                    {invoiceStatusLabel(inv.status)}
                  </Badge>
                </TableCell>
                <TableCell className="tabular-nums">
                  {formatMoney(inv.base_total, 'USD') ?? '—'}
                </TableCell>
                <TableCell className="text-muted-foreground hidden lg:table-cell">
                  {formatDate(inv.issue_date)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {inv.document ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-8"
                        aria-label={`View ${inv.number} PDF`}
                        title="View PDF"
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(inv);
                        }}
                      >
                        <Eye className="size-4" />
                      </Button>
                    ) : null}
                    <MarkPaidButton invoice={inv} />
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function InvoicesCards({ invoices, onOpen }: ListProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {invoices.map((inv) => (
        <Card
          key={inv.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(inv)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(inv);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{inv.number}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={invoiceStatusVariant(inv.status)}>
              {invoiceStatusLabel(inv.status)}
            </Badge>
            <span className="text-muted-foreground truncate">{partyName(inv)}</span>
            <span className="text-foreground ml-auto tabular-nums">
              {formatMoney(inv.base_total, 'USD') ?? '—'}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function InvoicesList({ invoices, onOpen }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {invoices.map((inv) => (
        <li key={inv.id}>
          {/* Use a real <button> so the row is interactive a11y-correctly. */}
          <button
            type="button"
            onClick={() => onOpen(inv)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">{inv.number}</span>
            <span className="text-muted-foreground hidden truncate text-xs sm:inline">
              {partyName(inv)}
            </span>
            <Badge variant={invoiceStatusVariant(inv.status)} className="shrink-0">
              {invoiceStatusLabel(inv.status)}
            </Badge>
            <span className="text-muted-foreground text-xs tabular-nums">
              {formatMoney(inv.base_total, 'USD') ?? '—'}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function InvoicesGroupedByStatus({ invoices, onOpen }: ListProps) {
  // Bucket by status, preserving the declared status order; skip empty groups.
  const groups = useMemo(() => {
    return INVOICE_STATUSES.map((s) => ({
      key: s.value,
      label: s.label,
      bucket: invoices.filter((inv) => inv.status === s.value),
    }));
  }, [invoices]);

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`invoices-status-${key}`}>
            <h2
              id={`invoices-status-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <InvoicesCards invoices={bucket} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function InvoicesSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

/** Inline "mark paid" action — only for issued invoices. Paying realizes the
 *  linked deal's sales commission (ADR-046). */
function MarkPaidButton({ invoice }: { invoice: Invoice }) {
  const markPaid = useMarkInvoicePaid(invoice.id);
  if (invoice.status !== 'issued') return null;
  return (
    <Button
      size="icon"
      variant="ghost"
      aria-label={`Mark ${invoice.number} paid`}
      title="Mark paid"
      disabled={markPaid.isPending}
      onClick={(e) => {
        e.stopPropagation();
        markPaid.mutate();
      }}
    >
      <CircleDollarSign className="size-4" />
    </Button>
  );
}
